import { RegionCode } from "./region.type";

export enum AmazonDomain {
  US = "amazon.com",
  CA = "amazon.ca",
  FR = "amazon.fr",
  UK = "amazon.co.uk",
  AU = "amazon.com.au",
  BR = "amazon.com.br",
  ES = "amazon.es",
  DE = "amazon.de",
  IT = "amazon.it",
  MX = "amazon.com.mx",
  IN = "amazon.in",
  NL = "amazon.nl",
  BE = "amazon.com.be",
  PL = "amazon.pl",
  SE = "amazon.se",
  JP = "amazon.co.jp",
}

// Mapping from RegionCode to Amazon domain
export const REGION_TO_AMAZON_DOMAIN: Record<RegionCode, AmazonDomain> = {
  [RegionCode.USA]: AmazonDomain.US,
  [RegionCode.Canada]: AmazonDomain.CA,
  [RegionCode.France]: AmazonDomain.FR,
  [RegionCode.UK]: AmazonDomain.UK,
  [RegionCode.Australia]: AmazonDomain.AU,
  [RegionCode.Brazil]: AmazonDomain.BR,
  [RegionCode.Portugal]: AmazonDomain.ES, // amazon.es is closest to Portugal
  [RegionCode.Germany]: AmazonDomain.DE,
  [RegionCode.Spain]: AmazonDomain.ES,
  [RegionCode.Italy]: AmazonDomain.IT,
  [RegionCode.Mexico]: AmazonDomain.MX,
  [RegionCode.Switzerland]: AmazonDomain.DE, // Swiss users typically shop amazon.de
  [RegionCode.Austria]: AmazonDomain.DE, // Austrian users shop amazon.de
  [RegionCode.Belgium]: AmazonDomain.BE,
  [RegionCode.Netherlands]: AmazonDomain.NL,
  [RegionCode.Poland]: AmazonDomain.PL,
  [RegionCode.Sweden]: AmazonDomain.SE,
  [RegionCode.Ireland]: AmazonDomain.UK, // Irish users shop amazon.co.uk
  [RegionCode.NewZealand]: AmazonDomain.AU, // NZ users shop amazon.com.au
  [RegionCode.India]: AmazonDomain.IN,
  [RegionCode.Argentina]: AmazonDomain.US, // No Amazon AR store; fallback to US
  [RegionCode.Colombia]: AmazonDomain.US, // No Amazon CO store; fallback to US
  [RegionCode.Chile]: AmazonDomain.US, // No Amazon CL store; fallback to US
  [RegionCode.Japan]: AmazonDomain.JP,
  [RegionCode.All]: AmazonDomain.US,
};

export const AMAZON_DOMAIN_TO_REGION_CODE: Record<AmazonDomain, RegionCode> = {
  [AmazonDomain.US]: RegionCode.USA,
  [AmazonDomain.CA]: RegionCode.Canada,
  [AmazonDomain.FR]: RegionCode.France,
  [AmazonDomain.UK]: RegionCode.UK,
  [AmazonDomain.AU]: RegionCode.Australia,
  [AmazonDomain.BR]: RegionCode.Brazil,
  [AmazonDomain.ES]: RegionCode.Spain,
  [AmazonDomain.DE]: RegionCode.Germany,
  [AmazonDomain.IT]: RegionCode.Italy,
  [AmazonDomain.MX]: RegionCode.Mexico,
  [AmazonDomain.IN]: RegionCode.India,
  [AmazonDomain.NL]: RegionCode.Netherlands,
  [AmazonDomain.BE]: RegionCode.Belgium,
  [AmazonDomain.PL]: RegionCode.Poland,
  [AmazonDomain.SE]: RegionCode.Sweden,
  [AmazonDomain.JP]: RegionCode.Japan,
};

export function getRegionCodeForAmazonDomain(domain: AmazonDomain): RegionCode {
  return AMAZON_DOMAIN_TO_REGION_CODE[domain] ?? RegionCode.USA;
}

/**
 * Check if an Amazon URL matches the expected region
 * @param amazonUrl - The Amazon URL to check
 * @param regionCode - The region code to match against
 * @returns true if the Amazon URL domain matches the region
 */
export function isAmazonUrlForRegion(
  amazonUrl: string,
  regionCode: RegionCode
): boolean {
  try {
    const url = new URL(amazonUrl);
    const expectedDomain = REGION_TO_AMAZON_DOMAIN[regionCode];

    // Check if the hostname matches the expected Amazon domain
    return url.hostname.includes(expectedDomain);
  } catch (error) {
    console.error("Error parsing Amazon URL:", error);
    return false;
  }
}

/**
 * Get the Amazon domain for a specific region
 * @param regionCode - The region code
 * @returns The Amazon domain for that region
 */
export function getAmazonDomainForRegion(regionCode: RegionCode): AmazonDomain {
  return REGION_TO_AMAZON_DOMAIN[regionCode];
}

/**
 * Audible storefront domains, keyed by the region's Amazon domain. Only the
 * marketplaces that actually have an Audible site are listed; regions without one
 * (Mexico, Netherlands, Belgium, Poland, Sweden) return undefined so callers can
 * fall back to the Amazon detail-page link for the Audible edition.
 */
const AMAZON_DOMAIN_TO_AUDIBLE_DOMAIN: Partial<Record<AmazonDomain, string>> = {
  [AmazonDomain.US]: "audible.com",
  [AmazonDomain.CA]: "audible.ca",
  [AmazonDomain.UK]: "audible.co.uk",
  [AmazonDomain.AU]: "audible.com.au",
  [AmazonDomain.FR]: "audible.fr",
  [AmazonDomain.DE]: "audible.de",
  [AmazonDomain.IT]: "audible.it",
  [AmazonDomain.ES]: "audible.es",
  [AmazonDomain.JP]: "audible.co.jp",
  [AmazonDomain.IN]: "audible.in",
  [AmazonDomain.BR]: "audible.com.br",
};

/**
 * Get the Audible storefront domain for a region (e.g. "audible.ca"), or undefined
 * when that marketplace has no Audible site.
 */
export function getAudibleDomainForRegion(
  regionCode: RegionCode
): string | undefined {
  return AMAZON_DOMAIN_TO_AUDIBLE_DOMAIN[getAmazonDomainForRegion(regionCode)];
}

/** Sample search URL for post-onboarding trial (any regional `www.{domain}` host). */
export function amazonOnboardingTrialSearchUrl(
  domain: AmazonDomain | string,
  keyword = "headphones"
): string {
  return `https://www.${domain}/s?k=${encodeURIComponent(keyword)}`;
}

/**
 * Top result tiles of an Amazon search page, used as "what the shopper is
 * looking at" context by the page generators. Kept tiny: the prompt already
 * carries other product context.
 */
export interface AmazonSerpHint {
  title: string;
  /** Formatted price string as Amazon renders it (e.g. "$24.99", "CDN$ 1,299"). */
  price?: string;
  /** True for tiles Amazon labels "Sponsored": paid placements, not organic relevance. */
  sponsored?: boolean;
}
